import {
	PRODUCT_TYPE_CONFIGURABLE,
	PRODUCT_TYPE_SIMPLE,
	PRODUCT_TYPE_GROUPED,
} from '@bluheadless/ui-logic/src/constants'
import { useProductContext } from '@bluheadless/ui-logic/src/providers/product'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { productMainImage, getAttributesByOptionsSelected } from '@bluheadless/ui-logic/src/utils/product'
import { useOosNotificationContext } from '@bluheadless/ui-logic/src/providers/oos-notification'
import { useNotification } from '@bluheadless/ui-logic/src/hooks/useNotification'
import { GroupedProductChildrenNotAddedError, ValidationError } from '@bluheadless/ui-logic/src/errors'
import { useMiniCartContext } from '@bluheadless/ui-logic/src/providers/minicart'
import {
	ButtonAddToCart,
	ButtonOutOfStock,
	PreOrderDescription,
	TypographyStyled,
	ButtonsActionWrapper,
	LeftWrapper,
	RightWrapper,
	Title,
} from './buttons-action.styled'
import { FormattedMessage, useIntl } from 'react-intl'
import { useCallback, useEffect, useMemo } from 'react'
import { useProductPriceProps } from '../../../molecules/product-price'
import { useCmsBlock } from '@bluheadless/ui-logic/src/hooks/cms/useCmsBlock'
import { useDateFormatter } from '@bluheadless/ui-logic/src/hooks/useDateFormatter'
import { cx } from '@emotion/css'
import { useTheme } from '@mui/system'
import useMediaQuery from '@mui/material/useMediaQuery'
import Price from '@bluheadless/ui/src/molecules/price/price'
import { useScrollTo } from '@bluheadless/ui-logic/src/hooks/useScrollTo'
import RelatedAttributes from '../sidebar/related-attributes'
import ProductOptions from '../../../molecules/product-options'

const ButtonsAction = ({ isSticky = false, stickyButtonsHeight, scrollToRef }) => {
	const { formatMessage, formatList } = useIntl()
	const productContextValue = useProductContext()
	const [, { openOosNotification }] = useOosNotificationContext()
	const { enqueueNotification } = useNotification()
	const productPriceProps = useProductPriceProps()

	const theme = useTheme()
	const lgUp = useMediaQuery(theme.breakpoints.up('lg'))

	const { setOpen: openMinicart } = useMiniCartContext()
	const {
		minicart: { productPageAutoOpen },
	} = useConfig()

	const {
		catalog: {
			products: {
				oosNotification: { enabled: enableOosNotifications },
				images: { width: imageWidth, height: imageHeight },
				imageType: { catalogBase },
			},
			swatches: { productPageAttributesProductImage },
		},
	} = useConfig()

	const [
		{
			assets,
			product: { name, type, configurableOptions, attributes },
			selectedProduct: {
				id: selectedProductId,
				sku: selectedProductSku,
				name: selectedProductName,
				attributes: selectedProductAttributes,
			} = {},
			inventory: { inStock, saleable, allowBackOrders },
			loadingAddToCart,
			selectedOptionsIdsValues,
		},
		{ addToCart, validateMinimalSelection },
	] = productContextValue

	const mainAsset = productMainImage(assets, catalogBase)

	const attributesSelected = useMemo(() => {
		return getAttributesByOptionsSelected(configurableOptions, selectedOptionsIdsValues)
	}, [configurableOptions, selectedOptionsIdsValues])

	const { manageScroll } = useScrollTo()
	const addToCartHandler = useCallback(
		async (e) => {
			try {
				await addToCart(() => {
					throw new ValidationError()
				}, e)
				enqueueNotification({
					uiProps: {
						message: formatMessage({ id: 'add_to_cart_success' }),
						severity: 'success',
					},
				})
				if (productPageAutoOpen) {
					openMinicart(true)
				}
			} catch (error) {
				if (error instanceof ValidationError) {
					// scroll to top to show error message
					manageScroll({ ref: scrollToRef })
					return
				}

				if (error instanceof GroupedProductChildrenNotAddedError) {
					enqueueNotification({
						enqueueProps: { key: 'cant_add_products_to_cart' },
						uiProps: {
							message: formatMessage(
								{ id: 'cant_add_products_to_cart' },
								{ productsNames: formatList(error.data.productsNotAdded.map((product) => product.name)) }
							),
							severity: 'error',
						},
					})

					if (error.data.productsNotAdded.length < error.data.childrenCount) {
						enqueueNotification({
							enqueueProps: { key: 'add_to_cart_success' },
							uiProps: {
								message: formatMessage({ id: 'add_to_cart_success' }),
								severity: 'success',
							},
						})

						if (productPageAutoOpen) {
							openMinicart(true)
						}
					}

					return
				}

				console.error(error?.response?.data?.error?.message ?? error)
				enqueueNotification({
					uiProps: {
						message: error?.response?.data?.error?.message || formatMessage({ id: 'add_to_cart_error' }),
						severity: 'error',
					},
				})
			}
		},
		[
			addToCart,
			enqueueNotification,
			formatMessage,
			productPageAutoOpen,
			openMinicart,
			manageScroll,
			scrollToRef,
			formatList,
		]
	)

	useEffect(() => {
		if (stickyButtonsHeight && isSticky) {
			document.documentElement.style.setProperty('--stickyButtonsHeight', stickyButtonsHeight - 2 + 'px')
		}
		return () => {
			document.documentElement.style.setProperty('--stickyButtonsHeight', '0px')
		}
	}, [isSticky, stickyButtonsHeight])

	const { content } = useCmsBlock({ identifier: 'pre-order-description' })
	const shippingDateFormatted = useDateFormatter(
		selectedProductAttributes?.date_shipping?.value ?? attributes?.date_shipping?.value
	)

	const StickyLeftInfo = useMemo(
		() =>
			isSticky ? (
				<LeftWrapper>
					<Title component="p" variant="h1">
						{selectedProductName ?? name}
					</Title>
					<Price {...productPriceProps} />
				</LeftWrapper>
			) : null,
		[isSticky, selectedProductName, name, productPriceProps]
	)

	const sizeOptions = useMemo(
		() => [
			...(configurableOptions
				?.filter(({ attributeCode }) => attributeCode === 'size')
				?.map(({ ...option }) => ({
					...option,
					renderer: 'select', // force select renderer
				})) ?? []),
		],
		[configurableOptions]
	)

	return inStock ? (
		saleable ? (
			<>
				{allowBackOrders ? (
					<>
						<PreOrderDescription content={content} />

						<TypographyStyled component="div" variant="body1">
							{shippingDateFormatted ? (
								<FormattedMessage id="back_orders_label_with_date" values={{ date: shippingDateFormatted }} />
							) : (
								<FormattedMessage id="back_orders_label" />
							)}
						</TypographyStyled>
					</>
				) : null}
				<ButtonsActionWrapper isSticky={isSticky}>
					{StickyLeftInfo}
					<RightWrapper>
						{type === PRODUCT_TYPE_CONFIGURABLE && isSticky && sizeOptions.length > 0 && (
							<>
								<RelatedAttributes isSticky isProductPage />
								<ProductOptions isSticky attributesProductImage={productPageAttributesProductImage} />
							</>
						)}
						<ButtonAddToCart
							disabled={loadingAddToCart}
							loading={loadingAddToCart}
							onClick={addToCartHandler}
							label={
								type == PRODUCT_TYPE_GROUPED
									? formatMessage({ id: 'action_add_all_to_cart' })
									: allowBackOrders
									? formatMessage({ id: 'action_add_to_cart_backorder' })
									: formatMessage({ id: 'action_add_to_cart' })
							}
							isSticky={isSticky}
							className={cx('add-to-cart')}
							fullWidth={!isSticky}
							StartIcon={null}
							size={isSticky && !lgUp ? 'small' : 'medium'}
						/>
					</RightWrapper>
				</ButtonsActionWrapper>
			</>
		) : null
	) : (
		enableOosNotifications && (type === PRODUCT_TYPE_CONFIGURABLE || type == PRODUCT_TYPE_SIMPLE) && (
			<ButtonsActionWrapper isSticky={isSticky}>
				{StickyLeftInfo}
				<RightWrapper>
					<ButtonOutOfStock
						fullWidth
						onClick={async () => {
							if (await validateMinimalSelection()) {
								openOosNotification({
									productId: selectedProductId,
									productSku: selectedProductSku,
									title1: selectedProductName,
									attributes: attributesSelected,
									priceProps: productPriceProps,
									image: {
										alt: mainAsset?.alt || selectedProductName,
										src: mainAsset?.image,
										originalWidth: imageWidth,
										originalHeight: imageHeight,
									},
								})
							}
						}}
						isSticky={isSticky}
						className={cx('oos-btn')}
					>
						{formatMessage({ id: 'notify_me_when_available' })}
					</ButtonOutOfStock>
				</RightWrapper>
			</ButtonsActionWrapper>
		)
	)
}

ButtonsAction.defaultProps = {
	stickyButtonsHeight: 60,
}

export default ButtonsAction
